/**
 * AudioEngine — the Web Audio mixing graph + the listener.
 *
 * We let three.js own the LISTENER: a `THREE.AudioListener` parented to the
 * active camera makes the browser's `AudioContext.listener` track the camera
 * pose for free (it updates from the camera world matrix each render). Because
 * the camera lives in render space (relative to the floating `origin`), any
 * emitter we place at `worldPos − origin` is automatically spatialised
 * correctly — the floating origin cancels out.
 *
 * On top of that we build a proper bus mixer so volumes, ducking and the
 * underwater muffle are first-class:
 *
 *     [ sfx ]  [ music ]  [ ambient ]  [ ui ]      (per-category gains)
 *         \________\___________/__________/
 *                        |
 *                     master  →  muffle(LP)  →  comp(limiter)  →  listener → out
 *
 * The graph is created with the context SUSPENDED; `resume()` (called from the
 * launch click, a real user gesture) unblocks playback per browser autoplay
 * policy. Nothing here throws if Web Audio is unavailable — callers no-op.
 */
import * as THREE from "three";
import {audioTarget} from './automation';

export type BusName = "sfx" | "music" | "ambient" | "ui";
export const BUSES: BusName[] = ["sfx", "music", "ambient", "ui"];

const MUFFLE_OPEN = 20000; // lowpass effectively bypassed above audible range
const MUFFLE_SHUT = 480; // fully submerged: everything goes dull

export class AudioEngine {
  readonly listener: THREE.AudioListener;
  readonly ctx: AudioContext;

  private master: GainNode;
  /** Master volume for the music bus, which bypasses the SFX compressor (see ctor). */
  private musicMaster: GainNode;
  private muffle: BiquadFilterNode;
  private comp: DynamicsCompressorNode;
  private bus: Record<BusName, GainNode>;
  /** User-set 0..1 volume per bus (before duck/master). */
  private busVol: Record<BusName, number> = { sfx: 0.9, music: 0.55, ambient: 0.7, ui: 0.8 };
  private masterVol = 0.9;
  private muted = false;
  private musicDuck = 1; // 0..1, multiplies the music bus (combat/stinger ducking)

  unlocked = false;

  constructor(camera: THREE.Object3D) {
    this.listener = new THREE.AudioListener();
    camera.add(this.listener);
    this.ctx = this.listener.context as AudioContext;

    // Master → muffle → limiter → listener input (which feeds destination).
    this.comp = this.ctx.createDynamicsCompressor();
    this.comp.threshold.value = -8;
    this.comp.knee.value = 6;
    this.comp.ratio.value = 6;
    this.comp.attack.value = 0.003;
    this.comp.release.value = 0.25;

    this.muffle = this.ctx.createBiquadFilter();
    this.muffle.type = "lowpass";
    this.muffle.frequency.value = MUFFLE_OPEN;
    this.muffle.Q.value = 0.3;

    this.master = this.ctx.createGain();
    this.master.gain.value = this.masterVol;
    this.master.connect(this.muffle);
    this.muffle.connect(this.comp);
    this.comp.connect(this.listener.getInput());

    this.bus = {
      sfx: this.ctx.createGain(),
      music: this.ctx.createGain(),
      ambient: this.ctx.createGain(),
      ui: this.ctx.createGain(),
    };
    // Music has its own glue compressor and safety limiter (synth/player.ts). The SFX
    // compressor's 3 ms attack tracks individual bass cycles and makes music gritty,
    // so the music bus skips it and only takes the master volume/mute.
    this.musicMaster = this.ctx.createGain();
    this.musicMaster.gain.value = this.masterVol;
    this.musicMaster.connect(this.listener.getInput());
    for (const b of BUSES) {
      this.bus[b].gain.value = this.busVol[b];
      this.bus[b].connect(b === "music" ? this.musicMaster : this.master);
    }
  }

  /** Node a voice/bed/track connects its output into. */
  busInput(name: BusName): GainNode {
    return this.bus[name];
  }

  /** Listener (render-space) position — emitters cull against this. */
  get listenerPos(): THREE.Vector3 {
    return this.listener.position;
  }

  now(): number {
    return this.ctx.currentTime;
  }

  /** Resume the context on a user gesture (autoplay policy). Idempotent. */
  async resume(): Promise<void> {
    if (this.ctx.state !== "running") {
      try {
        await this.ctx.resume();
      } catch {
        /* gesture not honoured yet — retried on the next call */
      }
    }
    this.unlocked = this.ctx.state === "running";
  }

  // --- Volume controls (smoothly ramped to avoid zipper noise) ---

  private applyBus(b: BusName): void {
    const v = this.muted ? 0 : this.busVol[b] * (b === "music" ? this.musicDuck : 1);
    this.bus[b].gain.setTargetAtTime(v, this.ctx.currentTime, 0.03);
  }

  setMasterVolume(v: number): void {
    this.masterVol = clamp01(v);
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.masterVol, this.ctx.currentTime, 0.03);
    this.musicMaster.gain.setTargetAtTime(this.muted ? 0 : this.masterVol, this.ctx.currentTime, 0.03);
  }

  setBusVolume(name: BusName, v: number): void {
    this.busVol[name] = clamp01(v);
    this.applyBus(name);
  }

  getBusVolume(name: BusName): number {
    return this.busVol[name];
  }

  getMasterVolume(): number {
    return this.masterVol;
  }

  setMuted(on: boolean): void {
    this.muted = on;
    this.master.gain.setTargetAtTime(on ? 0 : this.masterVol, this.ctx.currentTime, 0.02);
    this.musicMaster.gain.setTargetAtTime(on ? 0 : this.masterVol, this.ctx.currentTime, 0.02);
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Duck the music bus to `level` (0..1) over `ms` — for stingers/combat. */
  duckMusic(level: number, ms = 250): void {
    this.musicDuck = clamp01(level);
    const v = this.muted ? 0 : this.busVol.music * this.musicDuck;
    this.bus.music.gain.setTargetAtTime(v, this.ctx.currentTime, Math.max(0.01, ms / 1000 / 3));
  }

  /** Underwater muffle: 0 = open, 1 = fully submerged (low-pass clamps down). */
  setMuffle(amount: number): void {
    const a = clamp01(amount);
    // Log interpolation reads more natural across the audible band.
    const f = Math.exp(Math.log(MUFFLE_OPEN) * (1 - a) + Math.log(MUFFLE_SHUT) * a);
    audioTarget(this.muffle.frequency, f, this.ctx.currentTime, 0.08);
    audioTarget(this.muffle.Q, 0.3 + a * 2.5, this.ctx.currentTime, 0.08);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
